import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  CodeOutlined,
  DisconnectOutlined,
  DownOutlined,
  FileWordOutlined,
  ItalicOutlined,
  LinkOutlined,
  LoadingOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MinusOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Divider, Dropdown, Input, Modal, Select, Tooltip, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import { useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

import { blogImageExtensions, blogImageMaxBytes, uploadBlogContentImage } from '../../api/blog';
import { ApiError } from '../../api/errors';
import { Indent } from './indentExtension';
import { importWordDocument } from './wordImport';
import { BrandColors } from '../../theme/brand';

/** Cấp tiêu đề mục cho phép trong bài — tiêu đề bài đã là cấp 1 trên website. */
const headingLevels = [2, 3, 4] as const;

type HeadingLevel = (typeof headingLevels)[number];

const blockOptions: { value: HeadingLevel | 0; label: string }[] = [
  { value: 0, label: 'Đoạn văn' },
  { value: 2, label: 'Tiêu đề lớn' },
  { value: 3, label: 'Tiêu đề vừa' },
  { value: 4, label: 'Tiêu đề nhỏ' },
];

/**
 * Kiểu chữ của vùng soạn thảo.
 *
 * Viết dưới dạng một khối CSS nhúng vì nội dung bên trong do trình soạn thảo tự dựng — không gắn được
 * kiểu viết thẳng vào từng thẻ. Mọi luật đều bắt đầu bằng lớp của vùng soạn thảo nên không tràn ra ngoài.
 */
const editorCss = `
.erp-rich-text .ProseMirror { min-height: 420px; padding: 16px 20px; outline: none; font-size: 15px; line-height: 1.75; color: #171b24; }
.erp-rich-text .ProseMirror > * + * { margin-top: 0.75em; }
.erp-rich-text .ProseMirror p { margin: 0; }
.erp-rich-text .ProseMirror h2 { font-size: 1.5em; font-weight: 600; margin: 1.2em 0 0.4em; line-height: 1.35; }
.erp-rich-text .ProseMirror h3 { font-size: 1.25em; font-weight: 600; margin: 1.1em 0 0.35em; line-height: 1.4; }
.erp-rich-text .ProseMirror h4 { font-size: 1.08em; font-weight: 600; margin: 1em 0 0.3em; }
.erp-rich-text .ProseMirror ul, .erp-rich-text .ProseMirror ol { padding-left: 1.6em; margin: 0.5em 0; }
.erp-rich-text .ProseMirror li > p { margin: 0.15em 0; }
.erp-rich-text .ProseMirror blockquote { border-left: 3px solid #1769d2; background: #f4f8fe; margin: 0.8em 0; padding: 8px 16px; color: #4a5263; }
.erp-rich-text .ProseMirror pre { background: #171b24; color: #f5f5f5; padding: 12px 16px; border-radius: 6px; font-family: Consolas, monospace; font-size: 13px; overflow-x: auto; }
.erp-rich-text .ProseMirror code { background: rgba(0, 0, 0, 0.06); padding: 0.1em 0.35em; border-radius: 4px; font-family: Consolas, monospace; font-size: 0.9em; }
.erp-rich-text .ProseMirror pre code { background: none; padding: 0; color: inherit; }
.erp-rich-text .ProseMirror img { max-width: 100%; height: auto; border-radius: 6px; display: block; margin: 0.8em auto; }
.erp-rich-text .ProseMirror img.ProseMirror-selectednode { outline: 3px solid #1769d2; }
.erp-rich-text .ProseMirror a { color: #1769d2; text-decoration: underline; }
.erp-rich-text .ProseMirror hr { border: none; border-top: 1px solid #e6ebf2; margin: 1.5em 0; }
.erp-rich-text .ProseMirror .tableWrapper { overflow-x: auto; margin: 0.8em 0; }
.erp-rich-text .ProseMirror table { border-collapse: collapse; width: 100%; table-layout: fixed; }
.erp-rich-text .ProseMirror th, .erp-rich-text .ProseMirror td { border: 1px solid #d5dce6; padding: 6px 10px; vertical-align: top; position: relative; }
.erp-rich-text .ProseMirror th { background: #f7f9fc; font-weight: 600; text-align: left; }
.erp-rich-text .ProseMirror td > p, .erp-rich-text .ProseMirror th > p { margin: 0; }
.erp-rich-text .ProseMirror .selectedCell::after { content: ''; position: absolute; inset: 0; background: rgba(23, 105, 210, 0.12); pointer-events: none; }
`;

/** Đuôi file Word nhận được. File .doc đời cũ phải lưu lại thành .docx trước. */
const wordExtension = '.docx';

/** Kiểu đoạn tại con trỏ: cấp tiêu đề mục, hoặc 0 khi là đoạn văn thường. */
function activeBlock(editor: Editor): HeadingLevel | 0 {
  return headingLevels.find((level) => editor.isActive('heading', { level })) ?? 0;
}

/** Thêm https:// cho địa chỉ gõ thiếu; chặn mọi lược đồ ngoài http, https, mailto. */
function normalizeLink(raw: string): string | null {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return null;
  }

  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;

  return /^(https?:|mailto:)/i.test(withScheme) ? withScheme : null;
}

interface ToolButtonProps {
  readonly title: string;
  readonly icon: ReactNode;
  readonly onClick: () => void;
  readonly isActive?: boolean;
  readonly isDisabled?: boolean;
}

/** Một nút trên thanh công cụ; nút đang bật có nền xanh nhạt. */
function ToolButton({ title, icon, onClick, isActive = false, isDisabled = false }: ToolButtonProps): ReactNode {
  return (
    <Tooltip title={title} mouseEnterDelay={0.4}>
      <Button
        type="text"
        size="small"
        icon={icon}
        aria-label={title}
        aria-pressed={isActive}
        disabled={isDisabled}
        onClick={onClick}
        style={isActive ? { background: BrandColors.blueSoft, color: BrandColors.blue } : undefined}
      />
    </Tooltip>
  );
}

interface RichTextEditorProps {
  /** Nội dung ban đầu. Chỉ đọc một lần lúc dựng — đổi bài thì dựng lại bằng khóa khác. */
  readonly initialHtml: string;
  /** Gọi mỗi khi nội dung đổi; nhận chuỗi rỗng khi bài trống. Nên là hàm ổn định, như hàm đặt trạng thái. */
  readonly onChange: (html: string) => void;
}

/**
 * Trình soạn thảo nội dung bài blog, kiểu Word: tiêu đề mục, chữ đậm nghiêng, danh sách, trích dẫn,
 * link và ảnh.
 *
 * Ảnh được tải lên kho tệp công khai ngay lúc chèn, nên người viết thấy ảnh thật trong lúc soạn. Máy chủ
 * vẫn làm sạch toàn bộ HTML khi lưu — thanh công cụ ở đây chỉ quyết định thứ người viết tạo ra được, không
 * phải lớp bảo vệ.
 */
export function RichTextEditor({ initialHtml, onChange }: RichTextEditorProps): ReactNode {
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isImportingWord, setIsImportingWord] = useState(false);
  const [isAltOpen, setIsAltOpen] = useState(false);
  const [altDraft, setAltDraft] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const wordInput = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [...headingLevels] },
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
      Indent,
      TableKit.configure({ table: { resizable: false } }),
    ],
    content: initialHtml,
    // Tắt kiểm tra chính tả của trình duyệt: nó mặc định tiếng Anh nên gạch đỏ dưới mọi chữ tiếng Việt.
    editorProps: { attributes: { spellcheck: 'false' } },
    onUpdate: ({ editor: current }) => {
      onChange(current.isEmpty ? '' : current.getHTML());
    },
  });

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      isBold: current.isActive('bold'),
      isItalic: current.isActive('italic'),
      isUnderline: current.isActive('underline'),
      isStrike: current.isActive('strike'),
      isBulletList: current.isActive('bulletList'),
      isOrderedList: current.isActive('orderedList'),
      isBlockquote: current.isActive('blockquote'),
      isCodeBlock: current.isActive('codeBlock'),
      isLink: current.isActive('link'),
      isAlignCenter: current.isActive({ textAlign: 'center' }),
      isAlignRight: current.isActive({ textAlign: 'right' }),
      isAlignJustify: current.isActive({ textAlign: 'justify' }),
      isInTable: current.isActive('table'),
      isImage: current.isActive('image'),
      block: activeBlock(current),
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
      wordCount: current.getText().split(/\s+/).filter((word) => word !== '').length,
    }),
  });

  function openLinkDialog(): void {
    const href: unknown = editor.getAttributes('link').href;

    setLinkDraft(typeof href === 'string' ? href : '');
    setIsLinkOpen(true);
  }

  function applyLink(): void {
    if (linkDraft.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setIsLinkOpen(false);

      return;
    }

    const href = normalizeLink(linkDraft);

    if (href === null) {
      message.error('Link chỉ nhận địa chỉ http, https hoặc mailto.');

      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setIsLinkOpen(false);
  }

  async function insertImage(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];

    // Cho phép chọn lại đúng tệp vừa chọn ở lần sau.
    event.target.value = '';

    if (file === undefined) {
      return;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!(blogImageExtensions as readonly string[]).includes(extension)) {
      message.error(`Ảnh chỉ nhận tệp ${blogImageExtensions.join(', ')}.`);

      return;
    }

    if (file.size > blogImageMaxBytes) {
      message.error('Ảnh tối đa 5 MB.');

      return;
    }

    setIsUploading(true);

    try {
      const url = await uploadBlogContentImage(file);
      const alt = file.name.slice(0, file.name.lastIndexOf('.'));

      editor.chain().focus().setImage({ src: url, alt }).run();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không tải được ảnh lên.');
    } finally {
      setIsUploading(false);
    }
  }

  function openAltDialog(): void {
    const alt: unknown = editor.getAttributes('image').alt;

    setAltDraft(typeof alt === 'string' ? alt : '');
    setIsAltOpen(true);
  }

  function applyAlt(): void {
    editor.chain().focus().updateAttributes('image', { alt: altDraft.trim() }).run();
    setIsAltOpen(false);
  }

  /** Hỏi trước khi file Word đè lên nội dung đang soạn. */
  function confirmReplace(): Promise<boolean> {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Thay nội dung đang soạn?',
        content: 'Nội dung file Word sẽ thay toàn bộ nội dung hiện có của bài.',
        okText: 'Thay',
        cancelText: 'Không',
        onOk: () => { resolve(true); },
        onCancel: () => { resolve(false); },
      });
    });
  }

  async function importWord(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];

    // Cho phép chọn lại đúng file vừa chọn ở lần sau.
    event.target.value = '';

    if (file === undefined) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(wordExtension)) {
      message.error(
        'Chỉ nhận file .docx. File .doc đời cũ: mở bằng Word → Lưu thành (Save As) → chọn "Word Document (.docx)" rồi nhập lại.',
        8,
      );

      return;
    }

    if (!editor.isEmpty && !(await confirmReplace())) {
      return;
    }

    setIsImportingWord(true);

    const hideLoading = message.loading('Đang đọc file Word…', 0);

    try {
      const result = await importWordDocument(file, uploadBlogContentImage);

      editor.chain().focus().setContent(result.html, { emitUpdate: true }).run();

      const skipped: string[] = [];

      if (result.unsupportedImages > 0) {
        skipped.push(`${result.unsupportedImages.toString()} ảnh dạng hình vẽ của Word (EMF/WMF) web không hiển thị được`);
      }

      if (result.oversizedImages > 0) {
        skipped.push(`${result.oversizedImages.toString()} ảnh quá 5 MB`);
      }

      if (result.failedImages > 0) {
        skipped.push(`${result.failedImages.toString()} ảnh tải lên kho thất bại`);
      }

      if (skipped.length === 0) {
        const images = result.importedImages > 0 ? ` kèm ${result.importedImages.toString()} ảnh` : '';

        message.success(`Đã nhập nội dung file Word${images}. Xem lại rồi bấm lưu.`);
      } else {
        message.warning(
          `Đã nhập nội dung, nhưng bỏ qua ${skipped.join('; ')}. Chèn lại các ảnh đó bằng nút chèn ảnh.`,
          10,
        );
      }
    } catch {
      message.error('Không đọc được file này. File có thể bị hỏng, có mật khẩu, hoặc không phải .docx.');
    } finally {
      hideLoading();
      setIsImportingWord(false);
    }
  }

  const tableMenu: MenuProps['items'] = [
    {
      key: 'insert',
      label: 'Chèn bảng 3 × 3',
      icon: <TableOutlined />,
      onClick: () => { editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); },
    },
    { type: 'divider' },
    {
      key: 'addRow',
      label: 'Thêm hàng bên dưới',
      disabled: !state.isInTable,
      onClick: () => { editor.chain().focus().addRowAfter().run(); },
    },
    {
      key: 'addColumn',
      label: 'Thêm cột bên phải',
      disabled: !state.isInTable,
      onClick: () => { editor.chain().focus().addColumnAfter().run(); },
    },
    { type: 'divider' },
    {
      key: 'deleteRow',
      label: 'Xóa hàng',
      danger: true,
      disabled: !state.isInTable,
      onClick: () => { editor.chain().focus().deleteRow().run(); },
    },
    {
      key: 'deleteColumn',
      label: 'Xóa cột',
      danger: true,
      disabled: !state.isInTable,
      onClick: () => { editor.chain().focus().deleteColumn().run(); },
    },
    {
      key: 'deleteTable',
      label: 'Xóa cả bảng',
      danger: true,
      disabled: !state.isInTable,
      onClick: () => { editor.chain().focus().deleteTable().run(); },
    },
  ];

  return (
    <div className="erp-rich-text" style={{ border: `1px solid ${BrandColors.borderStrong}`, borderRadius: 8, overflow: 'hidden' }}>
      <style>{editorCss}</style>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          padding: '6px 8px',
          background: BrandColors.surfaceSubtle,
          borderBottom: `1px solid ${BrandColors.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <Tooltip title="Đổ nội dung một file Word (.docx) vào bài — giữ tiêu đề mục, đậm nghiêng, danh sách, bảng, ảnh, căn lề và thụt lề">
          <Button
            type="text"
            size="small"
            icon={isImportingWord ? <LoadingOutlined /> : <FileWordOutlined style={{ color: '#2b579a' }} />}
            disabled={isImportingWord}
            onClick={() => { wordInput.current?.click(); }}
          >
            Nhập từ Word
          </Button>
        </Tooltip>

        <Divider type="vertical" />

        <Select<HeadingLevel | 0>
          size="small"
          variant="borderless"
          style={{ width: 128 }}
          options={blockOptions}
          value={state.block}
          onChange={(value) => {
            if (value === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level: value }).run();
            }
          }}
        />

        <Divider type="vertical" />

        <ToolButton title="Đậm (Ctrl+B)" icon={<BoldOutlined />} isActive={state.isBold}
          onClick={() => { editor.chain().focus().toggleBold().run(); }} />
        <ToolButton title="Nghiêng (Ctrl+I)" icon={<ItalicOutlined />} isActive={state.isItalic}
          onClick={() => { editor.chain().focus().toggleItalic().run(); }} />
        <ToolButton title="Gạch chân (Ctrl+U)" icon={<UnderlineOutlined />} isActive={state.isUnderline}
          onClick={() => { editor.chain().focus().toggleUnderline().run(); }} />
        <ToolButton title="Gạch ngang" icon={<StrikethroughOutlined />} isActive={state.isStrike}
          onClick={() => { editor.chain().focus().toggleStrike().run(); }} />

        <Divider type="vertical" />

        <ToolButton title="Căn trái" icon={<AlignLeftOutlined />}
          isActive={!state.isAlignCenter && !state.isAlignRight && !state.isAlignJustify}
          onClick={() => { editor.chain().focus().unsetTextAlign().run(); }} />
        <ToolButton title="Căn giữa" icon={<AlignCenterOutlined />} isActive={state.isAlignCenter}
          onClick={() => { editor.chain().focus().setTextAlign('center').run(); }} />
        <ToolButton title="Căn phải" icon={<AlignRightOutlined />} isActive={state.isAlignRight}
          onClick={() => { editor.chain().focus().setTextAlign('right').run(); }} />
        <ToolButton title="Căn đều hai bên" icon={<MenuOutlined />} isActive={state.isAlignJustify}
          onClick={() => { editor.chain().focus().setTextAlign('justify').run(); }} />
        <ToolButton title="Lùi lề" icon={<MenuFoldOutlined />}
          onClick={() => { editor.chain().focus().outdent().run(); }} />
        <ToolButton title="Thụt lề" icon={<MenuUnfoldOutlined />}
          onClick={() => { editor.chain().focus().indent().run(); }} />

        <Divider type="vertical" />

        <ToolButton title="Danh sách chấm" icon={<UnorderedListOutlined />} isActive={state.isBulletList}
          onClick={() => { editor.chain().focus().toggleBulletList().run(); }} />
        <ToolButton title="Danh sách số" icon={<OrderedListOutlined />} isActive={state.isOrderedList}
          onClick={() => { editor.chain().focus().toggleOrderedList().run(); }} />
        <ToolButton
          title="Trích dẫn"
          icon={<span style={{ fontSize: 18, lineHeight: 1, fontFamily: 'Georgia, serif' }}>“</span>}
          isActive={state.isBlockquote}
          onClick={() => { editor.chain().focus().toggleBlockquote().run(); }}
        />
        <ToolButton title="Khối mã" icon={<CodeOutlined />} isActive={state.isCodeBlock}
          onClick={() => { editor.chain().focus().toggleCodeBlock().run(); }} />
        <ToolButton title="Đường kẻ ngang" icon={<MinusOutlined />}
          onClick={() => { editor.chain().focus().setHorizontalRule().run(); }} />

        <Divider type="vertical" />

        <ToolButton title="Chèn link" icon={<LinkOutlined />} isActive={state.isLink} onClick={openLinkDialog} />
        <ToolButton title="Gỡ link" icon={<DisconnectOutlined />} isDisabled={!state.isLink}
          onClick={() => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); }} />
        <ToolButton
          title="Chèn ảnh (JPG, PNG, WEBP, GIF — tối đa 5 MB)"
          icon={isUploading ? <LoadingOutlined /> : <PictureOutlined />}
          isDisabled={isUploading}
          onClick={() => { fileInput.current?.click(); }}
        />
        <Tooltip title={state.isImage ? 'Mô tả ảnh (alt) — Google đọc phần này' : 'Bấm chọn một ảnh trong bài trước'}>
          <Button
            type="text"
            size="small"
            disabled={!state.isImage}
            onClick={openAltDialog}
            style={{ fontSize: 12, fontWeight: 600 }}
          >
            Mô tả ảnh
          </Button>
        </Tooltip>
        <Dropdown menu={{ items: tableMenu }} trigger={['click']}>
          <Button
            type="text"
            size="small"
            aria-label="Bảng"
            style={state.isInTable ? { background: BrandColors.blueSoft, color: BrandColors.blue } : undefined}
          >
            <TableOutlined /> <DownOutlined style={{ fontSize: 10 }} />
          </Button>
        </Dropdown>

        <Divider type="vertical" />

        <ToolButton title="Xóa định dạng" icon={<ClearOutlined />}
          onClick={() => { editor.chain().focus().unsetAllMarks().clearNodes().run(); }} />
        <ToolButton title="Hoàn tác (Ctrl+Z)" icon={<UndoOutlined />} isDisabled={!state.canUndo}
          onClick={() => { editor.chain().focus().undo().run(); }} />
        <ToolButton title="Làm lại (Ctrl+Y)" icon={<RedoOutlined />} isDisabled={!state.canRedo}
          onClick={() => { editor.chain().focus().redo().run(); }} />

        <input
          ref={fileInput}
          type="file"
          accept={blogImageExtensions.join(',')}
          style={{ display: 'none' }}
          onChange={(event) => { void insertImage(event); }}
        />
        <input
          ref={wordInput}
          type="file"
          accept={`${wordExtension},application/vnd.openxmlformats-officedocument.wordprocessingml.document`}
          style={{ display: 'none' }}
          onChange={(event) => { void importWord(event); }}
        />
      </div>

      <EditorContent editor={editor} />

      <div style={{ padding: '4px 12px', borderTop: `1px solid ${BrandColors.border}`, background: BrandColors.surfaceSubtle, textAlign: 'right' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {state.wordCount.toLocaleString('vi-VN')} chữ
        </Typography.Text>
      </div>

      <Modal
        open={isLinkOpen}
        title="Chèn link"
        okText="Gắn link"
        cancelText="Đóng"
        onOk={applyLink}
        onCancel={() => { setIsLinkOpen(false); }}
        destroyOnHidden
      >
        <Input
          autoFocus
          placeholder="https://mvalab.vn/..."
          value={linkDraft}
          onChange={(event) => { setLinkDraft(event.target.value); }}
          onPressEnter={applyLink}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Bôi đen chữ trước rồi bấm chèn link. Để trống rồi bấm Gắn link để gỡ link. Link luôn mở ở tab mới.
        </Typography.Text>
      </Modal>

      <Modal
        open={isAltOpen}
        title="Mô tả ảnh (alt)"
        okText="Lưu mô tả"
        cancelText="Đóng"
        onOk={applyAlt}
        onCancel={() => { setIsAltOpen(false); }}
        destroyOnHidden
      >
        <Input
          autoFocus
          maxLength={200}
          showCount
          placeholder="Ví dụ: Camera kiểm tra lỗi trên dây chuyền đóng chai"
          value={altDraft}
          onChange={(event) => { setAltDraft(event.target.value); }}
          onPressEnter={applyAlt}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Một câu ngắn tả đúng nội dung ảnh. Google Hình ảnh và người dùng trình đọc màn hình đọc phần này.
        </Typography.Text>
      </Modal>
    </div>
  );
}
